import { useEffect, useMemo } from 'react';
import tw from 'twin.macro';

import { FilterControllerButton } from '@insights_module/components/episode_details_screen/FilterControllerButton';
import { useEpisodeSegment } from '@insights_module/hooks/useEpisodeSegment';
import { useEpisodeDetailsContext } from '@insights_module/context/EpisodeDetailsContext';
import { CaretIcon } from '@icons/CaretIcon';
import { CaretIconUp } from '@icons/CaretIconUp';
import { CloseIcon } from '@icons/CloseIcon';
import { scrollToSegment } from '@insights_module/utils/episodesUtils';
import { useEpisodeContextual } from '@insights_module/hooks/useEpisodeContextual';

const Container = tw.div`max-w-[400px] flex flex-row items-center bg-neutral-background rounded-sm px-2 py-1 gap-1 cursor-pointer`;
const CategoryLabel = tw.p`text-[11px] text-text-and-icon truncate`;
const SegmentCount = tw.p`text-[11px] text-text-and-icon-muted`;
const CloseButton = tw.div`flex justify-center items-center w-[12px] h-[12px] cursor-pointer`;

export const ContextualFilterController = () => {
  const {
    selectedContextualCategory,
    setSelectedContextualCategory,
    selectedSegment,
    setSelectedSegment,
  } = useEpisodeDetailsContext();
  const { segmentIdsFilteredByContextualCategory } = useEpisodeSegment();
  const { contextualData } = useEpisodeContextual();

  const selectedSegmentDisplayName = useMemo(
    () =>
      contextualData.find((element) => element.name === selectedContextualCategory)?.display_name ||
      selectedContextualCategory,
    [setSelectedContextualCategory, contextualData]
  );

  const selectedIndex = useMemo(() => {
    if (!segmentIdsFilteredByContextualCategory.length || selectedSegment === null) return -1;

    return segmentIdsFilteredByContextualCategory.indexOf(selectedSegment);
  }, [segmentIdsFilteredByContextualCategory, selectedSegment]);

  const handlePrev = () => {
    if (!selectedIndex) {
      setSelectedSegment(
        segmentIdsFilteredByContextualCategory[segmentIdsFilteredByContextualCategory.length - 1]
      );
      return;
    }

    setSelectedSegment(segmentIdsFilteredByContextualCategory[selectedIndex - 1]);
  };

  const handleNext = () => {
    if (selectedIndex === segmentIdsFilteredByContextualCategory.length - 1) {
      setSelectedSegment(segmentIdsFilteredByContextualCategory[0]);
      return;
    }

    setSelectedSegment(segmentIdsFilteredByContextualCategory[selectedIndex + 1]);
  };

  const handleClose = () => {
    setSelectedContextualCategory(null);
    setSelectedSegment(null);
  };

  const handleGoToSegment = () => {
    if (!selectedSegment) return;

    scrollToSegment(selectedSegment);
  };

  useEffect(() => {
    // If there is no selected segment, select the first segment in the list but only if selected segment is not in the filtered list already
    if (
      segmentIdsFilteredByContextualCategory.length &&
      (selectedSegment === null ||
        (selectedSegment && !segmentIdsFilteredByContextualCategory.includes(selectedSegment)))
    ) {
      setSelectedSegment(segmentIdsFilteredByContextualCategory[0]);
    }
  }, [segmentIdsFilteredByContextualCategory]);

  useEffect(() => {
    if (selectedSegment !== null) {
      scrollToSegment(selectedSegment);
    }
  }, [selectedIndex]);

  if (!selectedContextualCategory) return null;

  return (
    <Container>
      <CategoryLabel onClick={handleGoToSegment}>{selectedSegmentDisplayName}</CategoryLabel>

      <SegmentCount>{`${selectedIndex + 1}/${
        segmentIdsFilteredByContextualCategory.length
      }`}</SegmentCount>

      <FilterControllerButton onClick={handlePrev}>
        <CaretIconUp tw='stroke-text-and-icon w-2' />
      </FilterControllerButton>
      <FilterControllerButton onClick={handleNext}>
        <CaretIcon tw='fill-text-and-icon w-2' />
      </FilterControllerButton>

      <CloseButton onClick={handleClose}>
        <CloseIcon tw='fill-text-and-icon w-[8px] h-[8px]' />
      </CloseButton>
    </Container>
  );
};
